import { useCallback, useEffect, useRef, useState } from "react";

import {
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";

import useDisableScrolling from "@/components/hooks/useDisableScrolling";
import { cn } from "@/lib/utils";
import { withIkVideoQuality } from "@/lib/utils/marketing";

interface CinemaVideoModalProps {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function CinemaVideoModal({ open, onClose, url, title }: CinemaVideoModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  useDisableScrolling(open);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (!open) return;
    setIsMuted(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setShowControls(true);
    const vid = videoRef.current;
    if (vid) {
      vid.muted = false;
      vid.currentTime = 0;
      vid.play().catch(() => {});
    }
    return () => clearTimeout(hideTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) resetHideTimer();
  }, [open, resetHideTimer]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === " " || e.key === "k") {
        e.preventDefault();
        togglePlay();
      }
      if (e.key === "ArrowRight") skip(15);
      if (e.key === "ArrowLeft") skip(-15);
      if (e.key === "m") toggleMute();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPlaying]);

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play();
    } else {
      vid.pause();
    }
  };

  const skip = (seconds: number) => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = Math.max(0, Math.min(vid.duration, vid.currentTime + seconds));
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect || !videoRef.current) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = ratio * duration;
  };

  const handleClose = () => {
    const vid = videoRef.current;
    if (vid) vid.pause();
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        ref={containerRef}
        className="relative w-[90vw] max-w-5xl rounded-2xl overflow-hidden bg-black cursor-default"
        onMouseMove={resetHideTimer}
        onClick={(e) => e.stopPropagation()}
      >
        {/* close button */}
        <button
          onClick={handleClose}
          className={cn(
            "absolute top-3 right-3 z-20 flex items-center justify-center w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 transition-all",
            showControls ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          aria-label="Close video"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* video with aspect ratio */}
        <div className="relative aspect-video">
          <video
            ref={videoRef}
            src={withIkVideoQuality(url)}
            className="absolute inset-0 w-full h-full object-contain"
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
            onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
            playsInline
          />

          {/* bottom controls overlay */}
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 z-20 flex flex-col gap-3 px-5 py-3 transition-opacity duration-300",
              "bg-linear-to-t from-black/80 via-black/40 to-transparent",
              showControls ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            {/* title + time */}
            <div className="flex items-end justify-between">
              {title && <p className="text-white text-sm md:text-lg font-normal">{title}</p>}
              <p className="text-white text-xs md:text-sm font-normal whitespace-nowrap ml-4">
                {formatTime(currentTime)} / {formatTime(duration)}
              </p>
            </div>

            {/* progress bar */}
            <div
              ref={progressRef}
              className="relative h-[3px] w-full cursor-pointer group"
              onClick={handleProgressClick}
            >
              <div className="absolute inset-0 bg-white/40 rounded-full" />
              <div
                className="absolute left-0 top-0 h-full bg-white rounded-full"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `${progress}%`, marginLeft: "-6px" }}
              />
            </div>

            {/* controls row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <ControlButton onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  )}
                </ControlButton>
                <ControlButton onClick={() => skip(-15)} aria-label="Skip back 15 seconds">
                  <SkipBack className="w-5 h-5 text-white" />
                </ControlButton>
                <ControlButton onClick={() => skip(15)} aria-label="Skip forward 15 seconds">
                  <SkipForward className="w-5 h-5 text-white" />
                </ControlButton>
              </div>

              <div className="flex items-center gap-1">
                <ControlButton onClick={toggleMute} aria-label={isMuted ? "Unmute" : "Mute"}>
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </ControlButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ControlButton({ children, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(
        "flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors cursor-pointer",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}
